import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Inject, OnDestroy, Type, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MaterialModule } from '../../material.module';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TableColumnsProp } from '../../models/table.model';
import { SummaryTableComponent } from '../summary-table/summary-table.component';
import { ModalButton } from '../../models/dynamic.model';
import {
  addressBookSavePromptButtons,
  commercialInvoicePopupActionButtons,
  logoutPopupActionButtons,
  phoneCountryMismatchPopupButtons,
  countryChangeConfirmPopupButtons,
} from 'src/app/features/components/air-waybill/air-waybill.config';
import { FormControl, Validators } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { CommonService } from '../../services/common.service';
import { buildMultiPieceShipmentExt } from '../../utility/shipment-ext.util';
import { ErrorHandlerService } from 'src/app/core/services/error-handler.service';
import {
  AIRWAY_BILL_PRINT_MESSAGE,
  DIALOG_ACTIONS,
  MESSAGE_POPUP_SOURCE,
  ModalPopupMessages,
  StatusCodes,
  validationErrorMessages,
} from '../../utility/constants';
import { SnakbarService } from '../../services/snakbar.service';
import { CommercialInvoiceComponent } from '../commercial-invoice/commercial-invoice.component';
import { AirWaybillGenerationPreviewComponent } from '../air-waybill-generation-preview/air-waybill-generation-preview.component';
import { ValidatorPattern } from '../../utility/validators.pattern';

@Component({
  selector: 'app-message-popup',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    SvgIconComponent,
    SummaryTableComponent,
    CommercialInvoiceComponent,
    AirWaybillGenerationPreviewComponent,
  ],
  templateUrl: './message-popup.component.html',
  styleUrls: ['./message-popup.component.scss'],
})
export class MessagePopupComponent implements OnDestroy {
  @ViewChild(MatMenuTrigger) trigger!: MatMenuTrigger;

  public messagePopupData: any;
  public shipmentTrackerTableData!: any[];
  public shipmentGateTrackerGridColumns!: TableColumnsProp[];
  public displayedColumns: string[] = [];
  public injectedComponent: Type<any> | null = null;
  public isFrom!: string;
  public popupButtons: ModalButton[] = [];
  public email = new FormControl('', [
    Validators.pattern(ValidatorPattern.Email_Validator),
  ]);
  public emailList: string[] = [];
  public emailError = validationErrorMessages.invalidEmail;

  private shouldKeepOpen = true;
  private childWindowRef: Window | null = null;
  private menuReopenTimer?: ReturnType<typeof setTimeout>;
  private printDataSent = false;
  private printReadyHandler?: (event: MessageEvent) => void;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly ref: MatDialogRef<MessagePopupComponent>,
    private readonly commonService: CommonService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly snakbarService: SnakbarService,
    private readonly destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.messagePopupData = this.data;
    this.shipmentTrackerTableData =
      this.messagePopupData.code?.tableColumnDataSource ?? [];
    this.shipmentGateTrackerGridColumns =
      this.messagePopupData.code?.tableColumnConfig ?? [];
    this.displayedColumns = this.shipmentGateTrackerGridColumns
      .map(col => col.key)
      .concat('action');
    this.injectedComponent = this.messagePopupData.injectedComponent ?? null;
    this.isFrom = this.messagePopupData.code?.isFrom ?? '';
    this.setPopupButtons();
  }

  public closePopup(event?: string) {
    this.ref.close(false);
  }

  public proccedAndCloseShipmentSelection(event: any) {
    this.ref.close({
      popupClose: true,
      selectedShipmentData: event,
    });
  }

  public onPopupButtonClick(id: string) {
    if (id === 'cancel') {
      this.closePopup();
    } else if (id === 'print') {
      this.printPopup();
    } else if (id === 'ok') {
      this.ref.close(true);
    } else if (id === DIALOG_ACTIONS.CONTINUE) {
      this.ref.close(DIALOG_ACTIONS.CONTINUE);
    } else if (id === DIALOG_ACTIONS.YES) {
      this.ref.close(DIALOG_ACTIONS.YES);
    } else if (id === DIALOG_ACTIONS.NO) {
      this.ref.close(DIALOG_ACTIONS.NO);
    }
  }

  public reopenMenu(trigger: MatMenuTrigger) {
    if (this.shouldKeepOpen) {
      this.menuReopenTimer = setTimeout(() => {
        trigger.openMenu();
      }, 0);
    }
  }

  public sendMail() {
    if (this.emailList.length) {
      this.shouldKeepOpen = false;
      
      let scannerImages: string[] = [];
      const airwayBillData = this.messagePopupData.code.data;
      
      if (airwayBillData?.pieceDetails?.items && Array.isArray(airwayBillData.pieceDetails.items) && airwayBillData.pieceDetails.items.length > 0) {
        const baseHawbExt = airwayBillData?.hawbExt || '';
        scannerImages = [];
        let pageCounter = 0;
        
        airwayBillData.pieceDetails.items.forEach((item: any) => {
          const multiplier = Number(item.multiplier) || 1;
          
          for (let i = 0; i < multiplier; i++) {
            const uniqueHawbExt = buildMultiPieceShipmentExt(baseHawbExt, pageCounter);
            
            scannerImages.push(this.commonService.getBarcodeImage(uniqueHawbExt));
            pageCounter++;
          }
        });
      } else if (airwayBillData?.hawbExt) {
        scannerImages = [this.commonService.getBarcodeImage(airwayBillData.hawbExt)];
      }
      
      const payload = {
        scannerImage: scannerImages,
        emails: this.emailList,
      };
      this.commonService
        .shareAirwayBillViaEmail(payload, this.messagePopupData.code.data.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (data: any) => {
            if (data.statusCode == StatusCodes.Success) {
              this.snakbarService.openCustomisedSnackBar(
                data.message,
                ModalPopupMessages.success
              );
            } else {
              this.errorHandlerService.handleApiError(data);
            }
          },
        });
      this.trigger.closeMenu();
    }
  }

  public closeMenu() {
    this.shouldKeepOpen = false;
    this.emailList = [];
    this.trigger.closeMenu();
  }

  public addEmailToList() {
    const email = this.email?.value ?? '';
    if (this.email.valid && this.commonService.isValidField(email)) {
      this.emailList.push(email);
      this.email.reset();
      this.trigger.menuOpened.emit();
    }
  }

  public onRemoveIconClicked(index: number): void {
    this.emailList = this.emailList.filter((_, i) => i !== index);
  }

  private setPopupButtons() {
    if (this.isFrom === 'air-waybill-commercial-invoice') {
      this.popupButtons = commercialInvoicePopupActionButtons.map(btns => ({
        ...btns,
        ...(btns.id === 'cancel' && { label: 'Cancel' })
      }));
    } else if (this.isFrom === 'air-waybill-gen-preview') {
      this.popupButtons = commercialInvoicePopupActionButtons;
    } else if (this.isFrom === MESSAGE_POPUP_SOURCE.PHONE_COUNTRY_MISMATCH) {
      this.popupButtons = phoneCountryMismatchPopupButtons;
    } else if (this.isFrom === MESSAGE_POPUP_SOURCE.COUNTRY_CHANGE_CONFIRM) {
      this.popupButtons = countryChangeConfirmPopupButtons;
    } else if (this.isFrom === MESSAGE_POPUP_SOURCE.ADDRESS_BOOK_SAVE_PROMPT) {
      this.popupButtons = addressBookSavePromptButtons;
    } else if (this.messagePopupData.code?.isForWarning || this.messagePopupData.isForWarning) {
      this.popupButtons = logoutPopupActionButtons;
    }
  }

  printPopup(): void {
    this.printDataSent = false;
    this.detachPrintReadyListener();

    this.childWindowRef = window.open(
      `${window.location.origin}/airway-bill-print-preview`,
      'Child Window',
      'top=0,left=0,height=50%,width=auto'
    );

    if (!this.childWindowRef) {
      return;
    }

    this.messagePopupData.code.data.isFrom = this.messagePopupData.code.isFrom;

    this.printReadyHandler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== AIRWAY_BILL_PRINT_MESSAGE.READY) return;
      this.sendPrintDataOnce();
    };
    window.addEventListener('message', this.printReadyHandler);

    this.childWindowRef.postMessage(
      { type: AIRWAY_BILL_PRINT_MESSAGE.PING },
      window.location.origin
    );
  }

  ngOnDestroy(): void {
    if (this.menuReopenTimer) {
      clearTimeout(this.menuReopenTimer);
    }
    this.detachPrintReadyListener();
    if (this.childWindowRef && !this.childWindowRef.closed) {
      this.childWindowRef.close();
    }
    this.childWindowRef = null;
  }

  private sendPrintDataOnce(): void {
    if (this.printDataSent || !this.childWindowRef) {
      return;
    }
    this.printDataSent = true;
    this.detachPrintReadyListener();
    this.childWindowRef.postMessage(
      this.messagePopupData.code.data,
      window.location.origin
    );
  }

  private detachPrintReadyListener(): void {
    if (this.printReadyHandler) {
      window.removeEventListener('message', this.printReadyHandler);
      this.printReadyHandler = undefined;
    }
  }
}
